//----------------------------------------------------------------------------------------------------------------------
// Console Logger Backend
//----------------------------------------------------------------------------------------------------------------------

import type { LogLevel, LoggingBackend } from '../../interfaces/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

type ColorLevel = LogLevel | 'timer';

/**
 * A logging backend that logs to the console.
 */
export class ConsoleBackend implements LoggingBackend
{
    private timers : Map<string, number>;

    constructor()
    {
        this.timers = new Map();
    }

    /**
     * Get the CSS style for a specific log level
     */
    private getStyleForLevel(level : ColorLevel) : string
    {
        // CSS styles for browser console
        const styles : Record<ColorLevel, string> = {
            trace: 'color: #999999', // Dim gray
            debug: 'color: #00AAAA', // Cyan
            info: 'color: #00AA00', // Green
            warn: 'color: #AAAA00', // Yellow
            error: 'color: #AA0000', // Red
            timer: 'color: #AA00AA', // Magenta
            none: 'color: inherit', // Default
        };

        return styles[level] || styles.none;
    }

    /**
     * Format a log message with category, timestamp and log level
     */
    private formatMessage(category : string, level : ColorLevel) : [string, string, string, string]
    {
        // Format time as HH:MM:SS AM/PM
        const now = new Date();
        const hours = now.getHours() % 12 || 12; // Convert 0 to 12 for 12 AM
        const minutes = now.getMinutes().toString()
            .padStart(2, '0');
        const seconds = now.getSeconds().toString()
            .padStart(2, '0');
        const ampm = now.getHours() >= 12 ? 'PM' : 'AM';
        const timestamp = `[${ hours }:${ minutes }:${ seconds } ${ ampm }]`;

        const upperLevel = level.toUpperCase();

        // Return format strings and styling that will be used with console methods
        return [
            `${ timestamp } %c${ upperLevel }%c (${ category }): `, // Format string with placeholders
            this.getStyleForLevel(level), // Level style
            '', // Default style
            '', // Reset style
        ];
    }

    trace(category : string, message : string, ...args : unknown[]) : void
    {
        const [ format, defaultStyle, levelStyle, resetStyle ] = this.formatMessage(category, 'trace');
        console.debug(format, defaultStyle, levelStyle, resetStyle, message, ...args);
    }

    debug(category : string, message : string, ...args : unknown[]) : void
    {
        const [ format, defaultStyle, levelStyle, resetStyle ] = this.formatMessage(category, 'debug');
        console.debug(format, defaultStyle, levelStyle, resetStyle, message, ...args);
    }

    info(category : string, message : string, ...args : unknown[]) : void
    {
        const [ format, defaultStyle, levelStyle, resetStyle ] = this.formatMessage(category, 'info');
        console.info(format, defaultStyle, levelStyle, resetStyle, message, ...args);
    }

    warn(category : string, message : string, ...args : unknown[]) : void
    {
        const [ format, defaultStyle, levelStyle, resetStyle ] = this.formatMessage(category, 'warn');
        console.warn(format, defaultStyle, levelStyle, resetStyle, message, ...args);
    }

    error(category : string, message : string, ...args : unknown[]) : void
    {
        const [ format, defaultStyle, levelStyle, resetStyle ] = this.formatMessage(category, 'error');
        console.error(format, defaultStyle, levelStyle, resetStyle, message, ...args);
    }

    /**
     * Start a timer with the specified label.
     */
    time(category : string, label : string) : void
    {
        const timerKey = `${ category }:${ label }`;
        this.timers.set(timerKey, performance.now());
    }

    /**
     * End a timer and log the elapsed time.
     */
    timeEnd(category : string, label : string) : void
    {
        const timerKey = `${ category }:${ label }`;
        const startTime = this.timers.get(timerKey);

        if(startTime !== undefined)
        {
            const duration = performance.now() - startTime;
            this.timers.delete(timerKey);

            const [ format, defaultStyle, levelStyle, resetStyle ] = this.formatMessage(category, 'timer');
            console.info(
                `${ format } Timer '${ label }' completed in ${ duration.toFixed(2) }ms`,
                defaultStyle,
                levelStyle,
                resetStyle
            );
        }
        else
        {
            console.warn(`[${ category }]: Timer '${ label }' does not exist`);
        }
    }
}

//----------------------------------------------------------------------------------------------------------------------
